/**
 * HandleEditSheet.jsx
 * Bottom-sheet modal for editing the user's Connect-only @social handle.
 * This NEVER touches the system `name` field.
 *
 * Props:
 *  visible         – boolean
 *  currentHandle   – current value (string | null)
 *  onClose         – () => void
 *  onSaved         – (newHandle: string) => void
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    Modal,
    Pressable,
    TextInput,
    TouchableOpacity,
    Animated,
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform,
    Keyboard,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { updateSocialHandle } from '../../../api/social';

const HANDLE_REGEX = /^[a-zA-Z0-9_]{3,30}$/;

// Debounce helper
const useDebounce = (value, delay) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
};

const HandleEditSheet = ({ visible, currentHandle, onClose, onSaved }) => {
    const [handle, setHandle]       = useState('');
    const [error, setError]         = useState('');
    const [hint, setHint]           = useState('');
    const [saving, setSaving]       = useState(false);
    const slideAnim = useRef(new Animated.Value(300)).current;
    const inputRef  = useRef(null);

    // ── Sync handle when sheet opens ─────────────────────────────────────────
    useEffect(() => {
        if (visible) {
            setHandle(currentHandle || '');
            setError('');
            setHint('');
            Animated.spring(slideAnim, { toValue: 0, useNativeDriver: true, bounciness: 6 }).start();
            setTimeout(() => inputRef.current?.focus(), 350);
        } else {
            Animated.timing(slideAnim, { toValue: 300, duration: 220, useNativeDriver: true }).start();
        }
    }, [visible]);

    // ── Inline validation ────────────────────────────────────────────────────
    const debouncedHandle = useDebounce(handle, 350);
    useEffect(() => {
        const val = debouncedHandle.replace(/^@/, '').trim();
        if (!val) { setError(''); setHint(''); return; }
        if (val.length < 3) {
            setError('Too short — minimum 3 characters');
            setHint('');
            return;
        }
        if (val.length > 30) {
            setError('Too long — maximum 30 characters');
            setHint('');
            return;
        }
        if (!/^[a-zA-Z0-9_]+$/.test(val)) {
            setError('Only letters, numbers, and underscores allowed');
            setHint('');
            return;
        }
        setError('');
        setHint('Looks good!');
    }, [debouncedHandle]);

    // ── Save ─────────────────────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        const val = handle.replace(/^@/, '').trim();
        if (!HANDLE_REGEX.test(val)) {
            setError('Handle must be 3–30 characters: letters, numbers, underscores only.');
            return;
        }
        Keyboard.dismiss();
        setSaving(true);
        setError('');
        try {
            const result = await updateSocialHandle(val);
            onSaved?.(result.socialHandle || val);
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.error || 'Failed to save handle. Please try again.';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [handle, onSaved, onClose]);

    const cleanHandle = handle.replace(/^@/, '');
    const isValid = HANDLE_REGEX.test(cleanHandle);
    const hasChanged = cleanHandle !== (currentHandle || '');

    return (
        <Modal
            visible={visible}
            transparent
            animationType="none"
            onRequestClose={onClose}
            statusBarTranslucent
        >
            <KeyboardAvoidingView
                style={{ flex: 1 }}
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            >
                <Pressable style={styles.backdrop} onPress={onClose} />

                <Animated.View style={[styles.sheet, { transform: [{ translateY: slideAnim }] }]}>
                    {/* Handle pill */}
                    <View style={styles.pill} />

                    {/* Header */}
                    <View style={styles.header}>
                        <View>
                            <Text style={styles.title}>Edit @Handle</Text>
                            <Text style={styles.subtitle}>
                                Connect-only · your system name is never changed
                            </Text>
                        </View>
                        <TouchableOpacity onPress={onClose} style={styles.closeBtn} activeOpacity={0.7}>
                            <Ionicons name="close" size={20} color="#6B7280" />
                        </TouchableOpacity>
                    </View>

                    {/* Current handle display */}
                    {currentHandle ? (
                        <View style={styles.currentRow}>
                            <Ionicons name="at-circle-outline" size={16} color="#9CA3AF" />
                            <Text style={styles.currentLabel}>Current: </Text>
                            <Text style={styles.currentHandle}>@{currentHandle}</Text>
                        </View>
                    ) : (
                        <View style={styles.currentRow}>
                            <Ionicons name="information-circle-outline" size={16} color="#9CA3AF" />
                            <Text style={styles.currentLabel}>No handle set yet</Text>
                        </View>
                    )}

                    {/* Input */}
                    <View style={[
                        styles.inputRow,
                        error ? styles.inputRowError : (hint && isValid ? styles.inputRowOk : null)
                    ]}>
                        <Text style={styles.atSign}>@</Text>
                        <TextInput
                            ref={inputRef}
                            style={styles.input}
                            value={cleanHandle}
                            onChangeText={(t) => {
                                // Strip leading @ if user types it
                                setHandle(t.replace(/^@+/, ''));
                                setHint('');
                            }}
                            placeholder="your_handle"
                            placeholderTextColor="#9CA3AF"
                            autoCapitalize="none"
                            autoCorrect={false}
                            maxLength={31}
                            returnKeyType="done"
                            onSubmitEditing={handleSave}
                        />
                        {cleanHandle.length > 0 && (
                            <Text style={[styles.charCount, cleanHandle.length > 28 && styles.charCountWarn]}>
                                {cleanHandle.length}/30
                            </Text>
                        )}
                    </View>

                    {/* Feedback */}
                    {error ? (
                        <View style={styles.feedbackRow}>
                            <Ionicons name="alert-circle-outline" size={14} color="#EF4444" />
                            <Text style={styles.errorText}>{error}</Text>
                        </View>
                    ) : hint ? (
                        <View style={styles.feedbackRow}>
                            <Ionicons name="checkmark-circle-outline" size={14} color="#10B981" />
                            <Text style={styles.hintText}>{hint}</Text>
                        </View>
                    ) : (
                        <Text style={styles.rulesText}>
                            3–30 characters · letters, numbers, underscores · must be unique
                        </Text>
                    )}

                    {/* Actions */}
                    <View style={styles.actions}>
                        <TouchableOpacity
                            style={styles.cancelBtn}
                            onPress={onClose}
                            activeOpacity={0.75}
                            disabled={saving}
                        >
                            <Text style={styles.cancelText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={[styles.saveBtn, (!isValid || !hasChanged || saving) && styles.saveBtnDisabled]}
                            onPress={handleSave}
                            activeOpacity={0.85}
                            disabled={!isValid || !hasChanged || saving}
                        >
                            {saving ? (
                                <ActivityIndicator size="small" color="#FFFFFF" />
                            ) : (
                                <>
                                    <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                                    <Text style={styles.saveText}>Save Handle</Text>
                                </>
                            )}
                        </TouchableOpacity>
                    </View>
                </Animated.View>
            </KeyboardAvoidingView>
        </Modal>
    );
};

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.45)',
    },
    sheet: {
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingTop: 10,
        paddingBottom: Platform.OS === 'ios' ? 36 : 24,
        paddingHorizontal: 20,
    },
    pill: {
        width: 40,
        height: 4,
        backgroundColor: '#E5E7EB',
        borderRadius: 2,
        alignSelf: 'center',
        marginBottom: 18,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 14,
    },
    title: {
        fontSize: 18,
        fontWeight: '800',
        color: '#0F1419',
        letterSpacing: -0.3,
    },
    subtitle: {
        fontSize: 12,
        color: '#9CA3AF',
        marginTop: 2,
    },
    closeBtn: {
        padding: 4,
        marginTop: 2,
    },
    currentRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        backgroundColor: '#F9FAFB',
        borderRadius: 10,
        paddingHorizontal: 12,
        paddingVertical: 9,
        marginBottom: 14,
    },
    currentLabel: {
        fontSize: 13,
        color: '#9CA3AF',
    },
    currentHandle: {
        fontSize: 13,
        fontWeight: '600',
        color: '#374151',
    },
    inputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1.5,
        borderColor: '#E5E7EB',
        borderRadius: 14,
        paddingHorizontal: 14,
        paddingVertical: Platform.OS === 'ios' ? 13 : 10,
        backgroundColor: '#F9FAFB',
        gap: 6,
    },
    inputRowError: {
        borderColor: '#EF4444',
        backgroundColor: '#FEF2F2',
    },
    inputRowOk: {
        borderColor: '#10B981',
        backgroundColor: '#F0FDF4',
    },
    atSign: {
        fontSize: 17,
        fontWeight: '700',
        color: '#6B7280',
    },
    input: {
        flex: 1,
        fontSize: 17,
        fontWeight: '600',
        color: '#0F1419',
        padding: 0,
    },
    charCount: {
        fontSize: 12,
        color: '#9CA3AF',
        fontVariant: ['tabular-nums'],
    },
    charCountWarn: {
        color: '#F59E0B',
    },
    feedbackRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        marginTop: 8,
    },
    errorText: {
        fontSize: 13,
        color: '#EF4444',
        flex: 1,
    },
    hintText: {
        fontSize: 13,
        color: '#10B981',
        fontWeight: '600',
    },
    rulesText: {
        fontSize: 12,
        color: '#9CA3AF',
        marginTop: 8,
        lineHeight: 17,
    },
    actions: {
        flexDirection: 'row',
        gap: 10,
        marginTop: 22,
    },
    cancelBtn: {
        flex: 1,
        paddingVertical: 13,
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: '#E5E7EB',
        alignItems: 'center',
    },
    cancelText: {
        fontSize: 15,
        fontWeight: '600',
        color: '#374151',
    },
    saveBtn: {
        flex: 2,
        flexDirection: 'row',
        paddingVertical: 13,
        borderRadius: 14,
        backgroundColor: '#1D9BF0',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
    },
    saveBtnDisabled: {
        backgroundColor: '#BFDBFE',
    },
    saveText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#FFFFFF',
    },
});

export default HandleEditSheet;
